export function isValidFormId(formId) {
  return Boolean(formId && formId !== "new" && /^[a-f\d]{24}$/i.test(formId));
}

export function formDetailPath(id, section = "") {
  if (!id || id === "new") return "/app/forms/new";
  const base = `/app/forms/${id}`;
  if (!section || section === "overview") return base;
  return `${base}/${section}`;
}

// These already appear in the submissions table via the fixed Email/Name/
// Phone columns (read from submission.normalized), so their "show in table"
// checkbox should default to checked to reflect that, even though no extra
// dynamic column gets added for them.
export const IDENTITY_FIELD_NAMES = new Set([
  "email",
  "firstName",
  "lastName",
  "phoneNumber",
]);

export const DEFAULT_FORM_FIELDS = [
  {
    id: "fld_firstName",
    name: "firstName",
    type: "text",
    label: "Name",
    placeholder: "Your name",
    required: true,
    showInTable: true,
    order: 0,
    options: [],
  },
  {
    id: "fld_email",
    name: "email",
    type: "email",
    label: "Email",
    placeholder: "you@company.com",
    required: false,
    showInTable: true,
    order: 1,
    options: [],
  },
  {
    id: "fld_message",
    name: "message",
    type: "textarea",
    label: "Message",
    placeholder: "How can we help?",
    required: true,
    showInTable: false,
    order: 2,
    options: [],
  },
];

export const CONTACT_REQUIREMENT_OPTIONS = [
  { value: "email", label: "Email required" },
  { value: "phone", label: "Phone required" },
  { value: "email_or_phone", label: "Email or phone (at least one)" },
  { value: "none", label: "Neither required" },
];

export function contactRequirementToValidation(value) {
  switch (value) {
    case "email":
      return { requireOneOf: [["email"]] };
    case "phone":
      return { requireOneOf: [["phone"]] };
    case "email_or_phone":
      return { requireOneOf: [["email", "phone"]] };
    default:
      return { requireOneOf: [] };
  }
}

export function validationToContactRequirement(validation) {
  const groups = validation?.requireOneOf || [];
  if (!groups.length) return "none";
  const group = groups[0] || [];
  if (group.length === 1 && group[0] === "email") return "email";
  if (group.length === 1 && group[0] === "phone") return "phone";
  if (
    group.includes("email") &&
    group.includes("phone") &&
    group.length === 2
  ) {
    return "email_or_phone";
  }
  return "none";
}

function fieldValue(values, name) {
  const val = values?.[name];
  if (val === undefined || val === null) return "";
  if (typeof val === "boolean") return val ? "true" : "";
  // A file field's value is a File object (native input) or an antd
  // Upload fileList entry ({uid, name, size, ...}), never a plain string --
  // coercing it with String(val) would yield "[object Object]".
  if (val && typeof val === "object") {
    return val.name || val.uid ? "file" : "";
  }
  return String(val).trim();
}

function isValidEmail(value) {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(value);
}

function isValidPhone(value) {
  const digits = value.replace(/\D/g, "");
  return digits.length >= 10 && digits.length <= 15;
}

export function validateClientFormFields(fields, validation, values) {
  const errors = [];
  const sorted = [...(fields || [])].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0),
  );

  for (const field of sorted) {
    const value = fieldValue(values, field.name);
    // consent_marketing mirrors the API's own never-required treatment
    // (lib/forms/fieldSchema.js validateSubmissionFields) -- kept in sync
    // even though the field is already forced required:false at save
    // time, same defense-in-depth reasoning.
    const isCheckboxLike = field.type === "checkbox" || field.type === "consent_marketing";
    if (field.required && !value && !isCheckboxLike) {
      errors.push({
        field: field.name,
        message: `${field.label} is required.`,
      });
      continue;
    }
    if (!value && !isCheckboxLike) continue;
    if (field.type === "email" && value && !isValidEmail(value)) {
      errors.push({
        field: field.name,
        message: `${field.label} must be a valid email address.`,
      });
    }
    if (field.type === "phone" && value && !isValidPhone(value)) {
      errors.push({
        field: field.name,
        message: `${field.label} must be a valid phone number.`,
      });
    }
    if (field.type === "checkbox" && field.required && !values[field.name]) {
      errors.push({
        field: field.name,
        message: `${field.label} must be checked.`,
      });
    }
    if (field.type === "file" && value) {
      const file = values[field.name];
      const maxSizeBytes = field.maxSizeBytes || 5 * 1024 * 1024;
      if (Number.isFinite(file?.size) && file.size > maxSizeBytes) {
        errors.push({
          field: field.name,
          message: `${field.label} exceeds the maximum file size.`,
        });
      }
      if (
        field.acceptedMimeTypes?.length &&
        file?.type &&
        !field.acceptedMimeTypes.includes(String(file.type).toLowerCase())
      ) {
        errors.push({
          field: field.name,
          message: `${field.label} must be one of: ${field.acceptedMimeTypes.join(", ")}.`,
        });
      }
    }
  }

  for (const group of validation?.requireOneOf || []) {
    const satisfied = group.some((name) => fieldValue(values, name));
    if (!satisfied) {
      const labels = group
        .map((name) => sorted.find((f) => f.name === name)?.label || name)
        .join(" or ");
      errors.push({
        field: group.join(","),
        message: `Please provide at least one of: ${labels}.`,
      });
    }
  }

  return { ok: errors.length === 0, errors };
}

function slugFieldName(label) {
  const base = String(label || "field")
    .trim()
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, "_")
    .replace(/^_+|_+$/g, "");
  const name = base && /^[a-z]/.test(base) ? base : `field_${base || "custom"}`;
  return name.slice(0, 40);
}

export function newClientFieldId() {
  return `fld_${Math.random().toString(16).slice(2, 10)}${Date.now().toString(16).slice(-4)}`;
}

export function createFieldFromPreset(preset, existingNames = []) {
  const names = new Set(existingNames);
  const makeUnique = (base) => {
    let candidate = base;
    let i = 2;
    while (names.has(candidate)) {
      candidate = `${base}_${i}`;
      i += 1;
    }
    names.add(candidate);
    return candidate;
  };

  const presets = {
    email: {
      name: makeUnique("email"),
      type: "email",
      label: "Email",
      placeholder: "you@company.com",
      required: false,
      options: [],
    },
    phone: {
      name: makeUnique("phone"),
      type: "phone",
      label: "Phone",
      placeholder: "+1 555 000 0000",
      required: false,
      options: [],
    },
    name: {
      name: makeUnique("firstName"),
      type: "text",
      label: "Name",
      placeholder: "Your name",
      required: true,
      options: [],
    },
    message: {
      name: makeUnique("message"),
      type: "textarea",
      label: "Message",
      placeholder: "How can we help?",
      required: false,
      options: [],
    },
    text: {
      name: makeUnique("custom_field"),
      type: "text",
      label: "Custom field",
      placeholder: "",
      required: false,
      options: [],
    },
    textarea: {
      name: makeUnique("notes"),
      type: "textarea",
      label: "Notes",
      placeholder: "",
      required: false,
      options: [],
    },
    select: {
      name: makeUnique("choice"),
      type: "select",
      label: "Choose one",
      placeholder: "",
      required: false,
      options: [
        { value: "option_a", label: "Option A" },
        { value: "option_b", label: "Option B" },
      ],
    },
    file: {
      name: makeUnique("attachment"),
      type: "file",
      label: "Attachment",
      placeholder: "",
      required: false,
      options: [],
      maxSizeBytes: 5 * 1024 * 1024,
      acceptedMimeTypes: [],
    },
    // FR-3.6: rendered unticked, and never required -- both the hosted
    // form/embed (this field's own rendering) and the API
    // (lib/forms/fieldSchema.js) enforce the unticked-default/never-
    // required part regardless of what's saved here.
    consent_marketing: {
      name: makeUnique("marketingConsent"),
      type: "consent_marketing",
      label: "Send me occasional marketing emails",
      placeholder: "",
      required: false,
      options: [],
    },
  };

  const base = presets[preset] || presets.text;
  return {
    id: newClientFieldId(),
    showInTable: IDENTITY_FIELD_NAMES.has(base.name),
    ...base,
    label: base.label,
    name: base.name,
  };
}

export function normalizeClientFieldName(label, fallback = "field") {
  return slugFieldName(label || fallback);
}

export function resolveFormFieldsForUi(formRecord) {
  if (formRecord?.fields?.length) return formRecord.fields;
  return DEFAULT_FORM_FIELDS;
}

function renderEmbedInput(field) {
  const req = field.required ? " required" : "";
  const ph = field.placeholder
    ? ` placeholder="${field.placeholder.replace(/"/g, "&quot;")}"`
    : "";
  if (field.type === "textarea") {
    return `  <textarea name="${field.name}" rows="4"${ph}${req}></textarea>`;
  }
  if (field.type === "select") {
    const options = (field.options || [])
      .map(
        (opt) =>
          `    <option value="${String(opt.value).replace(/"/g, "&quot;")}">${String(opt.label || opt.value).replace(/</g, "&lt;")}</option>`,
      )
      .join("\n");
    return `  <select name="${field.name}"${req}>\n    <option value="">Select…</option>\n${options}\n  </select>`;
  }
  if (field.type === "checkbox" || field.type === "consent_marketing") {
    return `  <label><input type="checkbox" name="${field.name}" value="true" /> ${field.label.replace(/</g, "&lt;")}</label>`;
  }
  if (field.type === "file") {
    const accept = field.acceptedMimeTypes?.length
      ? ` accept="${field.acceptedMimeTypes.join(",")}"`
      : "";
    return `  <input type="file" name="${field.name}"${accept}${req} />`;
  }
  const type =
    field.type === "email"
      ? "email"
      : field.type === "phone"
        ? "tel"
        : "text";
  return `  <input type="${type}" name="${field.name}"${ph}${req} />`;
}

export function generateEmbedHtml({
  fields,
  actionUrl,
  tokenUrl,
  honeypot = "_gotcha",
}) {
  const sorted = [...(fields || [])].sort(
    (a, b) => (a.order ?? 0) - (b.order ?? 0),
  );
  const lines = sorted.flatMap((field) => {
    if (field.type === "checkbox" || field.type === "consent_marketing") {
      return [renderEmbedInput(field)];
    }
    return [
      `  <label>${field.label.replace(/</g, "&lt;")}</label>`,
      renderEmbedInput(field),
    ];
  });
  const hasFileField = sorted.some((field) => field.type === "file");
  const enctype = hasFileField ? ' enctype="multipart/form-data"' : "";

  return `<form action="${actionUrl}" method="POST"${enctype}>
${lines.join("\n")}
  <input type="hidden" name="antiSpamToken" id="mailinflow-antiSpamToken" value="" />
  <input type="text" name="${honeypot}" style="display:none" tabindex="-1" autocomplete="off" />
  <button type="submit">Submit</button>
</form>
<script>
(function () {
  fetch("${tokenUrl}")
    .then(function (r) { return r.json(); })
    .then(function (d) {
      if (d && d.token) {
        document.getElementById("mailinflow-antiSpamToken").value = d.token;
      }
    })
    .catch(function () {});
})();
</script>`;
}
